import type { EbayIntegrationStatus, EtsyIntegrationStatus } from "./repository";
import type { Marketplace, MarketplaceAccount } from "./types";

export interface ChannelPresentation {
  label: string;
  action: "Connect" | "Manage" | "Reconnect" | "Details" | "Set up";
  tone: "neutral" | "success" | "warning" | "danger" | "info";
  detail: string;
}

/** Connection identity is independent of publishing prerequisites and sync health. */
export function channelPresentation(input: {
  marketplace: Marketplace;
  direct: boolean;
  account?: MarketplaceAccount;
  ebay?: EbayIntegrationStatus;
  etsy?: EtsyIntegrationStatus;
  simulator?: boolean;
}): ChannelPresentation {
  const { marketplace, direct, account, simulator } = input;
  if (!direct) return { label: "Manual", action: "Manage", tone: "neutral", detail: "Publish in the marketplace and record the result in Listwork." };
  const provider = marketplace === "ebay" ? input.ebay : marketplace === "etsy" && !simulator ? input.etsy : undefined;
  if (marketplace === "ebay" || marketplace === "etsy" && !simulator) {
    if (!provider) return { label: "Checking connection", action: "Manage", tone: "neutral", detail: "Loading account status." };
    if (!provider.configured) return { label: "Not available", action: "Details", tone: "neutral", detail: "Connections are not available here yet." };
    if (!provider.connection) return { label: "Not connected", action: "Connect", tone: "neutral", detail: "Connect a seller account." };
    if (provider.connection.status === "reconnect_required" || !provider.authorization.authorized) return { label: "Reconnect needed", action: "Reconnect", tone: "warning", detail: "Renew access to resume checks and imports." };
    return { label: "Connected", action: "Manage", tone: "success", detail: "Manage your connected account." };
  }
  // Unavailable adapters must never suggest that the seller has broken a connection.
  if (!simulator || account?.environment === "production") return { label: "Not available", action: "Details", tone: "neutral", detail: "Direct connections are not available here yet." };
  if (account?.disconnectState === "failed" || account?.lifecycleState === "revocation_failed") return { label: "Disconnect failed", action: "Manage", tone: "danger", detail: "Try disconnecting again." };
  if (account?.authorizationState === "reauthorization_required") return { label: "Reconnect needed", action: "Reconnect", tone: "warning", detail: "Renew access to this test account." };
  if (account?.lifecycleState === "paused") return { label: "Paused", action: "Manage", tone: "neutral", detail: "This test connection is paused." };
  if (account?.authorizationState === "authorized" || account?.connectionStatus === "connected") return { label: "Connected", action: "Manage", tone: "success", detail: "Sample account; no live marketplace changes." };
  return { label: "Not connected", action: "Connect", tone: "neutral", detail: "Connect a test account." };
}
